import React, { useState, useEffect } from 'react';
import { FaChartLine, FaFileDownload, FaCalendar, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { balanceteAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { PDFDownloadLink } from '@react-pdf/renderer';
import BalancetePDF from './BalancetePDF';
import './Balancete.css';

const BalanceteView = () => {
  const currentDate = new Date();
  const [mes, setMes] = useState(currentDate.getMonth() + 1);
  const [ano, setAno] = useState(currentDate.getFullYear());
  const [balancete, setBalancete] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadBalancete = async () => {
    try {
      setLoading(true);
      const { data } = await balanceteAPI.getBalancete({ mes, ano });
      setBalancete(data.data);
    } catch (error) {
      toast.error('Erro ao carregar balancete');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getDayOfWeek = (dateString) => {
    const date = new Date(dateString);
    const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    return days[date.getDay()];
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  return (
    <div className="balancete-container">
      <div className="page-header">
        <div>
          <h1><FaChartLine /> Balancete Financeiro</h1>
          <p>Demonstrativo de receitas e despesas por período</p>
        </div>
      </div>

      {/* Filters */}
      <div className="balancete-filters">
        <div className="filter-section">
          <FaCalendar />
          <div className="filter-group">
            <label>Mês:</label>
            <select value={mes} onChange={(e) => setMes(Number(e.target.value))}>
              {monthNames.map((month, index) => (
                <option key={index} value={index + 1}>
                  {month}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Ano:</label>
            <select value={ano} onChange={(e) => setAno(Number(e.target.value))}>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <button onClick={loadBalancete} className="btn btn-primary" disabled={loading}>
            {loading ? 'Carregando...' : 'Gerar Balancete'}
          </button>

          {balancete && (
            <PDFDownloadLink
              document={<BalancetePDF data={balancete} />}
              fileName={`balancete-${mes}-${ano}.pdf`}
              className="btn btn-success"
            >
              {({ loading: pdfLoading }) => (
                <>
                  <FaFileDownload /> {pdfLoading ? 'Gerando PDF...' : 'Baixar PDF'}
                </>
              )}
            </PDFDownloadLink>
          )}
        </div>
      </div>

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregando balancete...</p>
        </div>
      )}

      {!loading && balancete && (
        <div className="balancete-content">
          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card receitas">
              <div className="card-icon">
                <FaArrowUp />
              </div>
              <div className="card-content">
                <span className="card-label">Receitas do Mês</span>
                <span className="card-value positive">
                  {formatCurrency(balancete.totais.receitas)}
                </span>
                <span className="card-count">
                  {balancete.lancamentos.filter(l => l.tipo === 'receita').length} lançamento(s)
                </span>
              </div>
            </div>

            <div className="summary-card despesas">
              <div className="card-icon">
                <FaArrowDown />
              </div>
              <div className="card-content">
                <span className="card-label">Despesas do Mês</span>
                <span className="card-value negative">
                  {formatCurrency(balancete.totais.despesas)}
                </span>
                <span className="card-count">
                  {balancete.lancamentos.filter(l => l.tipo === 'despesa').length} lançamento(s)
                </span>
              </div>
            </div>

            <div className="summary-card saldo">
              <div className="card-icon">
                <FaChartLine />
              </div>
              <div className="card-content">
                <span className="card-label">Saldo do Mês</span>
                <span className={`card-value ${balancete.totais.saldo >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(balancete.totais.saldo)}
                </span>
              </div>
            </div>
          </div>

          {/* Unified Table */}
          <div className="balancete-section">
            <h2 className="section-title">
              <FaChartLine /> MOVIMENTAÇÃO DO PERÍODO
            </h2>
            <div className="table-card">
              <table className="table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Dia</th>
                    <th>Descrição</th>
                    <th>Histórico</th>
                    <th className="text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {balancete.lancamentos.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center">
                        Nenhum lançamento registrado no período
                      </td>
                    </tr>
                  ) : (
                    balancete.lancamentos.map((lanc, index) => (
                      <tr 
                        key={`${lanc.tipo}-${lanc.id}-${index}`}
                        className={lanc.tipo === 'receita' ? 'row-receita' : 'row-despesa'}
                      >
                        <td>{formatDate(lanc.data)}</td>
                        <td className="text-muted">{getDayOfWeek(lanc.data)}</td>
                        <td><strong>{lanc.descricao}</strong></td>
                        <td className="text-muted">{lanc.historico || '-'}</td>
                        <td className={`text-right ${lanc.tipo === 'receita' ? 'value-positive' : 'value-negative'}`}>
                          {lanc.tipo === 'receita' ? '+' : '-'} {formatCurrency(lanc.valor)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Final Summary */}
          <div className="final-summary">
            <div className="summary-row">
              <span>Total de Receitas:</span>
              <span className="value-positive">{formatCurrency(balancete.totais.receitas)}</span>
            </div>
            <div className="summary-row">
              <span>Total de Despesas:</span>
              <span className="value-negative">{formatCurrency(balancete.totais.despesas)}</span>
            </div>
            <div className="summary-divider"></div>
            <div className="summary-row highlight">
              <span>SALDO DO PERÍODO:</span>
              <span className={balancete.totais.saldo >= 0 ? 'value-positive' : 'value-negative'}>
                {formatCurrency(balancete.totais.saldo)}
              </span>
            </div>
          </div>
        </div>
      )}

      {!loading && !balancete && (
        <div className="empty-state">
          <FaChartLine />
          <p>Selecione o mês e ano para gerar o balancete</p>
        </div>
      )}
    </div>
  );
};

export default BalanceteView;
